import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { supabase } from '../lib/supabase';
import { CATEGORIES, DB_FIELDS } from '../lib/constants';

const { FiUpload, FiArrowRight, FiCheckCircle, FiAlertCircle, FiSave, FiTrash2, FiLoader } = FiIcons;

const ImportWizard = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [fileData, setFileData] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [strategy, setStrategy] = useState('skip'); // skip, overwrite, copy
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    const { data } = await supabase.from('import_mappings').select('*');
    if (data) setPresets(data);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      setSheets(wb.SheetNames);
      setSelectedSheet(wb.SheetNames[0]);
      setFileData(wb);
      setStep(2);
    };
    reader.readAsBinaryString(file);
  };

  const processSheet = () => {
    const ws = fileData.Sheets[selectedSheet];
    const data = XLSX.utils.sheet_to_json(ws);
    setRawRows(data);
    setStep(3);
  };

  const handleMappingChange = (dbField, excelField) => {
    setMapping(prev => ({ ...prev, [dbField]: excelField }));
  };

  const savePreset = async () => {
    if (!presetName) return;
    await supabase.from('import_mappings').insert([{ name: presetName, mapping }]);
    setPresetName('');
    fetchPresets();
  };

  const loadPreset = (p) => {
    setMapping(p.mapping);
  };

  const generateNewId = async (category) => {
    const config = CATEGORIES[category] || { prefix: 'INV-' };
    const { count } = await supabase.from('inventory').select('id', { count: 'exact' }).eq('category', category);
    return `${config.prefix}${String((count || 0) + Date.now()).slice(-4)}`;
  };

  const startImport = async () => {
    setLoading(true);
    const success = [];
    const errors = [];
    
    for (let i = 0; i < rawRows.length; i++) {
      const row = rawRows[i];
      try {
        const payload = {};
        DB_FIELDS.forEach(f => {
          if (mapping[f.key]) payload[f.key] = row[mapping[f.key]];
        });

        if (!payload.category) throw new Error("Kategorie fehlt");
        
        // Handle ID
        let invId = payload.inventory_id;
        if (!invId) {
          invId = await generateNewId(payload.category);
          payload.inventory_id = invId;
        }

        // Check Duplicate
        const { data: existing } = await supabase.from('inventory').select('id').eq('inventory_id', invId).single();

        if (existing) {
          if (strategy === 'skip') continue;
          if (strategy === 'overwrite') {
            await supabase.from('inventory').update(payload).eq('id', existing.id);
          } else {
            payload.inventory_id = await generateNewId(payload.category);
            await supabase.from('inventory').insert([payload]);
          }
        } else {
          await supabase.from('inventory').insert([payload]);
        }
        success.push(invId);
      } catch (err) {
        errors.push({ row: i + 1, msg: err.message });
      }
    }

    setResults({ success: success.length, errors });
    setStep(5);
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="bg-slate-900 p-6 text-white flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black tracking-tight">Import-Assistent</h2>
          <p className="text-slate-400 text-xs uppercase font-bold tracking-widest mt-1">Schritt {step} von 5</p>
        </div>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} className={`w-2 h-2 rounded-full ${step >= s ? 'bg-yellow-500' : 'bg-slate-700'}`} />
          ))}
        </div>
      </div>

      <div className="p-8">
        {step === 1 && (
          <div className="text-center py-10">
            <label className="cursor-pointer group flex flex-col items-center">
              <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-yellow-50 transition-colors">
                <SafeIcon icon={FiUpload} className="text-3xl text-slate-400 group-hover:text-yellow-600" />
              </div>
              <span className="text-lg font-bold text-slate-800">Datei auswählen</span>
              <p className="text-slate-500 text-sm mt-1">Unterstützt .xlsx, .xls und .csv</p>
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h3 className="font-bold text-slate-800">Welches Tabellenblatt soll importiert werden?</h3>
            <div className="grid grid-cols-2 gap-3">
              {sheets.map(s => (
                <button 
                  key={s} 
                  onClick={() => setSelectedSheet(s)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${selectedSheet === s ? 'border-yellow-500 bg-yellow-50' : 'border-slate-100 hover:border-slate-200'}`}
                >
                  <span className="font-bold block">{s}</span>
                </button>
              ))}
            </div>
            <button onClick={processSheet} className="w-full bg-slate-900 text-white py-4 rounded-xl font-black flex items-center justify-center gap-2">
              Weiter zum Mapping <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="flex justify-between items-end border-b pb-4">
              <h3 className="font-black text-slate-800 uppercase text-xs tracking-widest">Spalten-Mapping</h3>
              <div className="flex gap-2">
                <select onChange={(e) => loadPreset(presets.find(p => p.id === e.target.value))} className="text-xs border rounded p-1">
                  <option value="">Vorlage laden...</option>
                  {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
              {DB_FIELDS.map(f => (
                <div key={f.key} className="flex items-center gap-4 bg-slate-50 p-3 rounded-lg">
                  <div className="flex-1">
                    <span className="text-xs font-bold text-slate-500 uppercase">{f.label} {f.required && '*'}</span>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="text-slate-300" />
                  <select 
                    value={mapping[f.key] || ''} 
                    onChange={(e) => handleMappingChange(f.key, e.target.value)}
                    className="flex-1 bg-white border border-slate-200 rounded p-1 text-sm"
                  >
                    <option value="">-- Nicht zugeordnet --</option>
                    {Object.keys(rawRows[0] || {}).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="flex gap-4 pt-4">
              <input 
                type="text" 
                placeholder="Name für neue Vorlage..." 
                className="flex-1 border rounded p-2 text-sm"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
              <button onClick={savePreset} className="bg-slate-100 px-4 rounded-lg text-slate-600 hover:bg-slate-200">
                <SafeIcon icon={FiSave} />
              </button>
            </div>

            <button onClick={() => setStep(4)} className="w-full bg-slate-900 text-white py-4 rounded-xl font-black flex items-center justify-center gap-2">
              Vorschau & Strategie <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
              <h4 className="text-blue-800 font-bold text-sm mb-2 flex items-center gap-2">
                <SafeIcon icon={FiAlertCircle} /> Duplikat-Regel (Inventar-ID)
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'skip', label: 'Überspringen' },
                  { id: 'overwrite', label: 'Überschreiben' },
                  { id: 'copy', label: 'Als Kopie' }
                ].map(s => (
                  <button 
                    key={s.id} 
                    onClick={() => setStrategy(s.id)}
                    className={`p-2 rounded-lg text-xs font-bold border transition-all ${strategy === s.id ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-blue-200 text-blue-600'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-[10px]">
                <thead className="bg-slate-100">
                  <tr>
                    {DB_FIELDS.slice(0, 4).map(f => <th key={f.key} className="p-2 text-left">{f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rawRows.slice(0, 3).map((row, idx) => (
                    <tr key={idx} className="border-t">
                      {DB_FIELDS.slice(0, 4).map(f => <td key={f.key} className="p-2 truncate max-w-[100px]">{row[mapping[f.key]] || '-'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 bg-slate-50 text-center text-slate-400 text-[10px]">
                Vorschau zeigt die ersten 3 von {rawRows.length} Zeilen
              </div>
            </div>

            <button 
              onClick={startImport} 
              disabled={loading}
              className="w-full bg-yellow-500 text-slate-900 py-4 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg hover:bg-yellow-600 transition-all"
            >
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheckCircle} />}
              IMPORT JETZT STARTEN
            </button>
          </div>
        )}

        {step === 5 && (
          <div className="text-center space-y-6 py-4">
            <div className="w-20 h-20 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheckCircle} className="text-4xl" />
            </div>
            <h3 className="text-2xl font-black text-slate-800">Import Abgeschlossen</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl">
                <span className="block text-2xl font-black text-slate-800">{results.success}</span>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Erfolgreich</span>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <span className="block text-2xl font-black text-red-500">{results.errors.length}</span>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Fehler</span>
              </div>
            </div>

            {results.errors.length > 0 && (
              <div className="text-left bg-red-50 p-4 rounded-xl border border-red-100 max-h-40 overflow-y-auto">
                <h4 className="text-red-800 font-bold text-xs mb-2 uppercase">Fehlerprotokoll:</h4>
                <ul className="text-[10px] text-red-600 space-y-1">
                  {results.errors.map((e, idx) => <li key={idx}>Zeile {e.row}: {e.msg}</li>)}
                </ul>
              </div>
            )}

            <button onClick={onComplete} className="w-full bg-slate-900 text-white py-4 rounded-xl font-black">
              Fertigstellen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportWizard;